import {skipToken} from '@reduxjs/toolkit/query';
import {isFeatureEnabled} from 'feature-flags';
import {Form, FormField, FormSubmitHandler, Separator, showNotification} from 'platform/components';
import {Box, Grid, Heading, Show, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import {DeepPartial} from 'react-hook-form';
import {useParams} from 'react-router-dom';

import {isNil, isNotNil} from 'ramda';

import {
  useCreateUserMutation,
  useGetCurrentUserInfoQuery,
  useGetOtherUserSettingsQuery,
  useGetUserQuery,
  usePatchUserMutation,
  useSetOtherUserSettingsItemMutation,
} from '@dms/api/customer';
import {UserResponseBody} from '@dms/api/shared';
import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';
import {settingsRoutes, testIds} from '@dms/routes';
import {SettingsFooter, SettingsSection, SettingsTemplate} from '@dms/settings/shared';
import {
  catchUnhandledDataGridActions,
  DmsDataGrid,
  handleApiError,
  is409UserDeactivatedError,
  isSystemUser,
  isSystemUserModificationError,
  openReactivateUserModal,
  usePermissions,
} from '@dms/shared';

import {ApiError, buildObject, isNotNilOrEmpty, useNavigate} from 'shared/web';

import {UserBranches} from './components/UserBranches';
import {UserInfo} from './components/UserInfo';
import {UserRoles} from './components/UserRoles';
import {MANAGER_ROLE_NAME} from './constants/MANAGER_ROLE_NAME';
import {useSettingsHeaderActions} from './hooks/useSettingsHeaderActions';
import {FormValues, getSchema} from './types/FormValues';

type SubmitDataType = {
  firstName: string;
  lastName: string;
  roles: string[];
  defaultBranchId: string;
  branchIds: string[];
};

export function UserDetail() {
  const {id: userId} = useParams();
  const isCreating = isNil(userId);
  const navigate = useNavigate();

  const {data: user, isLoading, isError} = useGetUserQuery({id: userId ?? ''}, {skip: !userId});
  const {data: currentUser} = useGetCurrentUserInfoQuery();

  const [canReadUserLoginHistory] = usePermissions({
    permissionKeys: ['readUserLoginHistory'],
  });

  const isCurrentUserManager = currentUser?.roles?.some((item) => item.title === MANAGER_ROLE_NAME);
  const isViewedUserManger = user?.roles?.some((role) => role.title === MANAGER_ROLE_NAME);
  const shouldIncludeRoles = isCurrentUserManager || !isViewedUserManger;

  const [patchUser] = usePatchUserMutation();
  const [createUser] = useCreateUserMutation();
  const [saveOtherUserSetting] = useSetOtherUserSettingsItemMutation();

  const {data: userSettings} = useGetOtherUserSettingsQuery(
    isNotNil(userId) ? {userId} : skipToken
  );

  const defaultValues: DeepPartial<FormValues> = {
    branchIds: user?.branches?.map((item) => item.id) ?? [],
    defaultBranchId: user?.defaultBranch?.id,
    roles: user?.roles?.map((item) => item.id) ?? [],
    firstName: user?.firstName,
    lastName: user?.lastName,
    insurerNumber: userSettings?.insurerNumber?.value?.toString() ?? undefined,
    customId: userSettings?.customId?.value?.toString() ?? undefined,
  };

  const actions = useSettingsHeaderActions(user);
  const isReadOnly = isSystemUser(user);

  const updateOtherSettings = (data: FormValues) => async (updateData: UserResponseBody) => {
    const id = isCreating ? updateData.id : userId;

    if ((data.customId || null) !== (defaultValues.customId || null)) {
      await saveOtherUserSetting({
        userId: id,
        settingKey: 'custom_id',
        body: {
          value: data.customId ?? '',
        },
      })
        .unwrap()
        .catch(handleApiError);
    }

    if ((data.insurerNumber || null) !== (defaultValues.insurerNumber || null)) {
      await saveOtherUserSetting({
        userId: id,
        settingKey: 'insurer_number',
        body: {
          value: data.insurerNumber ?? '',
        },
      })
        .unwrap()
        .catch(handleApiError);
    }
  };

  const handleSubmit: FormSubmitHandler<FormValues> = async (
    data,
    _setErrors,
    _reset,
    blockerContext
  ) => {
    const handleUpdate = () => {
      const submitData = buildObject<SubmitDataType>()
        .firstName(data.firstName)
        .lastName(data.lastName)
        .roles(data.roles, shouldIncludeRoles)
        .defaultBranchId(data.defaultBranchId)
        .branchIds(data.branchIds)
        .build();

      return isNil(userId)
        ? createUser({
            createUserRequestBody: {
              ...submitData,
              password: data.password as string,
              email: data.email as string,
            },
          })
        : patchUser({
            userId,
            updateUserRequestBody: submitData,
          });
    };

    await handleUpdate()
      .unwrap()
      .then(updateOtherSettings(data))
      .then(() => {
        if (blockerContext?.wasSubmittedFromBlocker) {
          return;
        }
        navigate(settingsRoutes.userManagement);
      })
      .catch((error: ApiError) => {
        const {email, password} = data;

        if (
          is409UserDeactivatedError(error) &&
          isNotNilOrEmpty(email) &&
          isNotNilOrEmpty(password)
        ) {
          openReactivateUserModal({
            reactivateUserRequestBody: {
              email,
              password,
              firstName: data.firstName,
              lastName: data.lastName,
              roles: data.roles,
              defaultBranchId: data.defaultBranchId,
              branchIds: data.branchIds,
            },
          });

          return;
        }

        match(error)
          .when(isSystemUserModificationError, () => {
            showNotification.error(i18n.t('entity.user.notifications.systemUserNotEditable'));
            // The blocker navigates away as soon as this handler resolves - reporting the failed
            // submit keeps the rejected changes on screen instead of silently discarding them.
            blockerContext?.reportApiFailure?.();
          })
          .otherwise((apiError) =>
            handleApiError(apiError, {signalApiError: blockerContext?.reportApiFailure})
          );
      });
  };

  const loginHistoryUserId =
    isFeatureEnabled(featureFlags.USER_LOGIN_HISTORY) &&
    isNotNil(userId) &&
    (userId === currentUser?.id || canReadUserLoginHistory)
      ? userId
      : null;

  return (
    <SettingsTemplate
      isCreating={isCreating}
      header={{
        title: user?.email ?? i18n.t('page.employeeDetail.labels.createNewUser'),
        breadcrumbs: [
          {label: i18n.t('entity.user.labels.users'), href: settingsRoutes.userManagement},
        ],
        actions,
        alerts: isReadOnly
          ? [
              {
                variant: 'info',
                message: i18n.t('page.settings.labels.systemUserReadOnlyInfo'),
              },
            ]
          : undefined,
      }}
      data-testid={testIds.settings.userManagementDetailV2('UserDetail')}
      isLoading={isLoading}
      isError={isError}
    >
      <SettingsSection>
        <Form<FormValues>
          defaultValues={defaultValues}
          onSubmit={handleSubmit}
          experimentalZodSchema={getSchema(isCreating ? 'create' : 'update')}
          shouldWatchForUnsavedChanges
        >
          {(control, formApi, navigateWithoutBlocker) => {
            const handleCancel = () => navigateWithoutBlocker(settingsRoutes.userManagement);

            return (
              <>
                <UserInfo
                  data-testid={testIds.settings.userManagementDetailV2('UserInfo')}
                  control={control}
                  isCreating={isCreating}
                  isDisabled={isReadOnly}
                />

                <Separator />

                <UserBranches
                  data-testid={testIds.settings.userManagementDetailV2('UserBranches')}
                  control={control}
                  formApi={formApi}
                  isDisabled={isReadOnly}
                />

                <Separator />

                <UserRoles
                  data-testid={testIds.settings.userManagementDetailV2('UserRoles')}
                  userId={userId}
                  control={control}
                  formApi={formApi}
                  isDisabled={isReadOnly}
                />

                <Separator />

                <Grid columns={2}>
                  <FormField
                    control={control}
                    type="text"
                    name="insurerNumber"
                    label={i18n.t('page.settings.labels.insurerNumber')}
                    isDisabled={isReadOnly}
                  />

                  <FormField
                    control={control}
                    type="text"
                    name="customId"
                    label={i18n.t('page.settings.labels.customUserId')}
                    isDisabled={isReadOnly}
                  />
                </Grid>

                <SettingsFooter
                  actions={[
                    {
                      variant: 'secondary',
                      type: 'button',
                      onClick: handleCancel,
                      title: isReadOnly
                        ? i18n.t('general.actions.back')
                        : i18n.t('general.actions.cancel'),
                      'data-testid': testIds.settings.userManagementDetailV2('cancel'),
                    },
                    ...(isReadOnly
                      ? []
                      : [
                          {
                            type: 'form-button',
                            title: isCreating
                              ? i18n.t('general.actions.create')
                              : i18n.t('general.actions.save'),
                            'data-testid': testIds.settings.userManagementDetailV2('submit'),
                            control,
                            buttonType: 'submit',
                          } as const,
                        ]),
                  ]}
                />
              </>
            );
          }}
        </Form>
      </SettingsSection>
      <Show when={loginHistoryUserId}>
        {(scopedUserId) => (
          <SettingsSection>
            <VStack spacing={4}>
              <Heading size={4}>{i18n.t('entity.user.labels.loginHistory')}</Heading>
              <Box height={100}>
                <DmsDataGrid
                  key={scopedUserId}
                  gridCode="user-login-history"
                  queryModifier={(filter) => ({...filter, userId: scopedUserId})}
                  actionCallback={({actionKey}) => catchUnhandledDataGridActions(actionKey)}
                  data-testid={testIds.settings.userManagementDetailV2('login-history')}
                />
              </Box>
            </VStack>
          </SettingsSection>
        )}
      </Show>
    </SettingsTemplate>
  );
}
